import { COUNTRIES, type Country } from "./countries.g.js";

/**
 * Phone numbers: one country picker, one national number, one stored string.
 *
 * ── Stored as E.164, always ────────────────────────────────────────────────
 *
 * `+919876543210` - a plus, then digits, and nothing else. It is the only
 * notation that is unambiguous without knowing where the reader is, which
 * matters here because a stop-work escalation dials a supervisor from whatever
 * country the person escalating happens to be in. Free text was what this
 * column held before, and "555 0100" is not a number anybody can call.
 *
 * ── The dial code is never typed ───────────────────────────────────────────
 *
 * It comes from the picker, and the input holds the **national** number alone.
 * So typing "12355" is the number and can never be mistaken for a country code,
 * and nothing has to guess a country from the digits. Same division as
 * visionai-app's `PhoneField`, which is where this behaviour is copied from.
 *
 * ── Why this file exists rather than a library ─────────────────────────────
 *
 * The console could use `react-international-phone`, as visionai-app does. The
 * Flutter client cannot use anything from npm, and a number entered on the
 * handset has to produce the byte-identical string to one entered in the
 * console. So the *data* is vendored from that library (`countries.json`,
 * generated into both languages) and the ~40 lines of logic over it live here
 * and in `mobile-app/lib/core/util/phone.dart`, which is a line-for-line port.
 * Two clients agreeing matters more here than one of them having a dependency.
 */

export { COUNTRIES, type Country };

/** Where a number goes when nothing else is known. */
export const DEFAULT_COUNTRY = "us";

/**
 * E.164: a plus, a non-zero leading digit, then up to 14 more.
 *
 * Fifteen digits is the ITU maximum and seven is about the shortest real
 * subscriber number once a dial code is on it. Deliberately not a
 * per-country length check - those change, and refusing to store a number
 * somebody can actually be reached on is worse than storing an odd one.
 */
export const E164 = /^\+[1-9]\d{6,14}$/;

export function isE164(value: string): boolean {
  return E164.test(value);
}

const digitsOf = (value: string) => value.replace(/\D/g, "");

const byIso2 = new Map(COUNTRIES.map((country) => [country.iso2, country]));

export function countryOf(iso2: string): Country | undefined {
  return byIso2.get(iso2.toLowerCase());
}

/**
 * A stored value split back into the picker's country and the input's digits.
 *
 * **A value that is not E.164 is not discarded.** Rows written before this
 * existed hold things like "555 0100", and a field that showed them as empty
 * would quietly delete a supervisor's number the first time somebody edited
 * their name. So anything without a leading plus keeps its digits as the
 * national number under `fallback`, and the reader can see what is there and
 * fix the country.
 *
 * The country search walks `COUNTRIES`, which the generator sorted longest dial
 * code first, so `+1876` finds Jamaica before it can fall through to `+1`. The
 * five countries that share `+1` **and** have no longer dial code of their own
 * are told apart by area code: a Canadian number is `+1` plus one of 42
 * prefixes, and without that check every one of them renders under the United
 * States flag.
 */
export function parsePhone(
  value: string | null | undefined,
  fallback: string = DEFAULT_COUNTRY
): { iso2: string; national: string } {
  const raw = (value ?? "").trim();
  if (!raw) return { iso2: fallback, national: "" };

  if (!raw.startsWith("+")) return { iso2: fallback, national: digitsOf(raw) };

  const digits = digitsOf(raw);
  let match: Country | undefined;

  for (const country of COUNTRIES) {
    if (!digits.startsWith(country.dial)) continue;
    // An area-code match is more specific than the dial code alone, so it wins
    // outright. Otherwise keep the first plain match - the list is already in
    // dial-length then priority order, which puts `us` ahead of `ca` for a
    // number belonging to neither.
    if (country.areaCodes) {
      const area = digits.slice(country.dial.length, country.dial.length + 3);
      if (country.areaCodes.includes(area)) return national(country, digits);
      continue;
    }
    match ??= country;
  }

  return match ? national(match, digits) : { iso2: fallback, national: digits };
}

function national(country: Country, digits: string) {
  return { iso2: country.iso2, national: digits.slice(country.dial.length) };
}

/**
 * The picker plus the input, back into one stored string.
 *
 * An empty national number is an **empty result**, not a bare dial code: "+91"
 * is not a phone number, and storing one would make every untouched optional
 * field look filled in.
 */
export function toE164(iso2: string, national: string): string {
  const digits = digitsOf(national);
  const country = countryOf(iso2);
  if (!digits || !country) return "";
  return `+${country.dial}${digits}`;
}

/**
 * National digits through the country's grouping mask.
 *
 * `.` in the mask consumes one digit; anything else is punctuation. Digits past
 * the end of the mask are appended plainly rather than dropped - a mask is a
 * hint about the usual shape of a number, not a length limit, and truncating
 * what somebody typed is the one unrecoverable thing this could do.
 */
export function formatNational(iso2: string, national: string): string {
  const digits = digitsOf(national);
  const mask = countryOf(iso2)?.format;
  if (!mask || !digits) return digits;

  let out = "";
  let index = 0;
  for (const char of mask) {
    if (index >= digits.length) break;
    out += char === "." ? digits[index++] : char;
  }
  return index < digits.length ? out + digits.slice(index) : out;
}

/** How many digits the mask expects, for a placeholder. Zero where there is no mask. */
export function nationalLength(iso2: string): number {
  const mask = countryOf(iso2)?.format;
  return mask ? [...mask].filter((char) => char === ".").length : 0;
}

/**
 * A stored number for reading: `+91 98765-43210`.
 *
 * The dial code stays separate from the national part so the country is legible
 * at a glance, which is the whole reason it is stored with one. Anything
 * unparseable is returned as it was rather than mangled into a shape it is not.
 */
export function formatPhone(value: string | null | undefined): string {
  const raw = (value ?? "").trim();
  if (!raw.startsWith("+")) return raw;

  const { iso2, national } = parsePhone(raw);
  const country = countryOf(iso2);
  if (!country || !national) return raw;
  return `+${country.dial} ${formatNational(iso2, national)}`;
}
